import { useEffect } from 'react';

/**
 * Fires the primary footer action when the user presses Enter inside an open
 * modal, unless focus is on an interactive text element (input, textarea,
 * select, or any contentEditable node). Mirrors the Button component's own
 * guards so Enter can't bypass loading/disabled states.
 *
 * @param {Object} params
 * @param {boolean} params.isOpen - Controlled open state
 * @param {boolean} [params.isFooter] - Whether the footer is rendered
 * @param {Object} [params.button] - The primary footer action config
 */

const useModalEnterKey = ({ isOpen, isFooter = true, button }) => {
  useEffect(() => {
    if (!isOpen || !isFooter || typeof button?.onClick !== 'function') return;

    const handleKeyDown = (e) => {
      if (e.key !== 'Enter') return;
      if (e.repeat) return;
      if (e.isComposing) return;
      if (e.defaultPrevented) return;
      if (button.isLoading || button.isDisabled) return;

      const el = document.activeElement;
      const tag = el?.tagName;

      if (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        tag === 'BUTTON' ||
        tag === 'A' ||
        el?.isContentEditable ||
        el?.getAttribute('role') === 'button'
      )
        return;

      e.preventDefault();
      button.onClick();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isFooter, button]);
};

export default useModalEnterKey;
